import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { WORLD } from '../../../core/config';
import { foundationHeight, footDepth } from '../foundation';
import { createMeshBuilder } from '../../mesh/meshBuilder';
import { elevatedBuilder } from '../../mesh/elevatedBuilder';
import { footedBuilder } from '../../mesh/footedBuilder';
import { meshBarriers, type Levels } from '../../mesh/planBarriers';
import { landmarkFooting } from './landmarkFooting';
import { buildWaterGate, type WaterGateColors } from './waterGate';

/* Collision reads the geometry; the materials do not affect its shape. */
const UNPAINTED: WaterGateColors = {
  wall: [0, 0, 0],
  roof: [0, 0, 0],
  opening: [0, 0, 0],
};

/** The pedestrian wings sit above a perpendicular water vault. Only masonry at the hull's
 * or crew's height obstructs that vault, on the same foundation as the rendered gatehouse. */
export function waterGateBarriers(
  landmarks: readonly Landmark[],
  bands: readonly Levels[],
  elevationAt: (p: Vec2) => number = () => WORLD.groundY
): readonly (readonly Ring[])[] {
  const perGate = landmarks
    .filter((landmark) => landmark.kind === 'waterGate')
    .map((landmark) => {
      const height = foundationHeight(landmark.footprint, elevationAt);
      const depth = footDepth(landmarkFooting(landmark), elevationAt, height);
      const raw = createMeshBuilder();
      const builder = elevatedBuilder(footedBuilder(raw, height, depth), height);
      buildWaterGate(builder, landmark, UNPAINTED, { edgeId: 0, seed: 0 }, () => undefined, height);
      const geometry = raw.finish();
      const barriers = meshBarriers(geometry, bands);
      geometry.dispose();
      return barriers;
    });
  return bands.map((_, band) => perGate.flatMap((rings) => rings[band]!));
}
